import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingCart, Favorite, FavoriteBorder } from "@mui/icons-material";
import { BranchProduct } from "../../../types";
import { useProductContext } from "../../../context/ProductContext";

interface CompactProductCardProps {
  product: BranchProduct;
  getCardColors: string;
  t: (key: string) => string;
}

const CompactProductCard: React.FC<CompactProductCardProps> = ({
  product,
  getCardColors,
  t,
}) => {
  const navigate = useNavigate();
  const { addToCart } = useProductContext();
  const [isHovered, setIsHovered] = useState(false);
  const [isWishlist, setIsWishlist] = useState(false);

  const handleAddToCart = (qty: number) => {
    for (let i = 0; i < qty; i++) addToCart(product.product.id);
  };

  const toggleWishlist = () => setIsWishlist(!isWishlist);

  const stockStatus = () => {
    if (!product.is_available || product.stock <= 0) return "bg-red-100 text-red-700";
    if (product.stock <= 3) return "bg-yellow-100 text-yellow-700";
    return "bg-green-100 text-green-700";
  };

  const images = product.product.images || [];

  return (
    <div
      data-product-id={product.id}
      className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Image */}
      <div
        className="relative aspect-square cursor-pointer"
        onClick={() => navigate(`/product/${product.product.id}`)}
      >
        <img
          src={images[0]?.image || "/api/placeholder/300/300"}
          alt={product.product.name}
          className="w-full h-full object-cover"
        />

        {/* Wishlist */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            toggleWishlist();
          }}
          className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm rounded-full p-1.5 shadow-sm"
        >
          {isWishlist ? <Favorite className="w-4 h-4 text-red-500" /> : <FavoriteBorder className="w-4 h-4 text-gray-600" />}
        </button>
      </div>

      {/* Content */}
      <div className="p-3">
        <h3 className="font-medium text-gray-800 text-sm line-clamp-1 mb-1">
          {product.product.name}
        </h3>

        <div className="flex items-center justify-between mb-2">
          <span className="text-lg font-bold text-primary">ج.م {product.price}</span>
          <span className={`text-xs px-2 py-0.5 rounded-full ${stockStatus()}`}>{product.stock}</span>
        </div>

        <button
          className={`w-full py-2 bg-gradient-to-r ${getCardColors} text-white text-sm font-medium rounded-lg hover:opacity-90 transition-all duration-300`}
          onClick={() => handleAddToCart(1)}
          disabled={!product.is_available}
        >
          <ShoppingCart className="w-4 h-4 inline mr-1" />
          {product.is_available ? t("productCard.addToCart") : t("productCard.outOfStock")}
        </button>
      </div>
    </div>
  );
};

export default CompactProductCard;
